<template>
  <div>
    <Modal
      :open='isOpen'
      title='Tickets'
      modal-classes='w-1/3 h-1/2 p-0 absolute'
      title-classes='text-left border-b px-8 pt-5 pb-4'
      content-classes='px-4 pt-5 bg-gray-50 overflow-y-auto pb-20'
      @close-modal='closeModal'
    >
      <template #content>
        <LoaderSpinner v-if='loading'/>

        <div
          v-else-if='tickets.length === 0'
          class='text-md text-center text-gray-500'
        >
          No available tickets.
        </div>

        <div
          v-else
          class='grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
        >
          <div
            v-for='ticket in tickets'
            :key='ticket.id'
            role='button'
            tabindex='0'
            :class='[
              "flex flex-col justify-between rounded-lg bg-white p-4 shadow transition-shadow duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400",
              loading ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:shadow-lg"
            ]'
            @click='!loading && use(ticket)'
          >
            <div class='border-t border-gray-100 text-sm'>
              <div class='text-gray-500'>
                Ticket #{{ ticket.id }}
              </div>

              <div class='mt-1 font-medium text-blue-600'>
                {{ ticket.duration }}h access
              </div>
            </div>
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref, watch } from 'vue'
  import { getAvailable, useTicket } from '@/api/requests/tickets/ticketsRequests'
  import { Ticket } from '@/types/api/tickets/ticketsTypes'
  import Modal from '@/components/Modal.vue'
  import LoaderSpinner from '@/components/LoaderSpinner.vue'
  import { useAuthStore } from '@/store/authentication'

  export default defineComponent({
    name: 'TicketAccessModal',
    components: {
      Modal,
      LoaderSpinner,
    },
    props: {
      visible: {
        type: Boolean,
        default: false,
        required: true,
      },
    },
    setup(props) {
      const isOpen = ref(false)
      const loading = ref(false)
      const tickets = ref<Ticket[]>([])
      const authStore = useAuthStore()

      const closeModal = () => {
        isOpen.value = false
      }

      const loadTickets = async () => {
        loading.value = true

        getAvailable().then((response) => {
          tickets.value = response
          loading.value = false
        })
      }

      const use = async (ticket: Ticket) => {
        loading.value = true

        useTicket(ticket.id).then((response) => {
          if (response.ticket_access_id) {
            authStore.setTicket(response.ticket_access_id)
          }

          loading.value = false
          isOpen.value = false
        })
      }

      watch(() => props.visible, async (newValue) => {
        isOpen.value = newValue

        if (newValue) {
          await loadTickets()
        }
      }, {
        immediate: true,
      })

      return {
        isOpen,
        closeModal,
        loading,
        tickets,
        use,
      }
    },
  })

</script>
